import type {
  AnalyticsDistributionParams,
  AnalyticsKpiParams,
  AnalyticsKpiResult,
  AnalyticsLeaderboardParams,
  AnalyticsMetric,
  AnalyticsRow,
  AnalyticsTimeSeriesParams,
  SdkAnalyticsHandle,
  SdkAnalyticsResource,
  SdkUseAnalyticsOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

type AnalyticsKind = 'kpi' | 'timeSeries' | 'leaderboard' | 'distribution';
type AnalyticsRequest =
  | [
      resource: SdkAnalyticsResource<'kpi'>,
      kind: 'kpi',
      params: AnalyticsKpiParams,
      options?: SdkUseAnalyticsOptions
    ]
  | [
      resource: SdkAnalyticsResource<'timeSeries'>,
      kind: 'timeSeries',
      params: AnalyticsTimeSeriesParams,
      options?: SdkUseAnalyticsOptions
    ]
  | [
      resource: SdkAnalyticsResource<'leaderboard'>,
      kind: 'leaderboard',
      params: AnalyticsLeaderboardParams,
      options?: SdkUseAnalyticsOptions
    ]
  | [
      resource: SdkAnalyticsResource<'distribution'>,
      kind: 'distribution',
      params: AnalyticsDistributionParams,
      options?: SdkUseAnalyticsOptions
    ];
type AnalyticsResult = AnalyticsKpiResult | AnalyticsRow[];

const ANALYTICS_KINDS = ['kpi', 'timeSeries', 'leaderboard', 'distribution'] as const;

function assertAnalyticsKind(kind: string): asserts kind is AnalyticsKind {
  if (!ANALYTICS_KINDS.includes(kind as AnalyticsKind)) {
    throw new Error(
      `Sdk.hooks.useAnalytics received unsupported analytics kind '${kind}'. Expected one of: ${ANALYTICS_KINDS.join(', ')}.`
    );
  }
}

export function useAnalytics<const TMetrics extends readonly AnalyticsMetric[]>(
  resource: SdkAnalyticsResource<'kpi'>,
  kind: 'kpi',
  params: AnalyticsKpiParams<TMetrics>,
  options?: SdkUseAnalyticsOptions
): SdkAnalyticsHandle<AnalyticsKpiResult<TMetrics>>;
export function useAnalytics(
  resource: SdkAnalyticsResource<'timeSeries'>,
  kind: 'timeSeries',
  params: AnalyticsTimeSeriesParams,
  options?: SdkUseAnalyticsOptions
): SdkAnalyticsHandle<AnalyticsRow[]>;
export function useAnalytics(
  resource: SdkAnalyticsResource<'leaderboard'>,
  kind: 'leaderboard',
  params: AnalyticsLeaderboardParams,
  options?: SdkUseAnalyticsOptions
): SdkAnalyticsHandle<AnalyticsRow[]>;
export function useAnalytics(
  resource: SdkAnalyticsResource<'distribution'>,
  kind: 'distribution',
  params: AnalyticsDistributionParams,
  options?: SdkUseAnalyticsOptions
): SdkAnalyticsHandle<AnalyticsRow[]>;
export function useAnalytics(...args: AnalyticsRequest): SdkAnalyticsHandle<AnalyticsResult> {
  const [resource, kind, params, options] = args;
  assertAnalyticsKind(kind);
  const queryClient = useQueryClient();
  const queryKey = ['analytics', resource.id, kind, params];

  const query = useQuery<AnalyticsResult>({
    enabled: options?.enabled ?? true,
    queryKey,
    queryFn: () => {
      switch (kind) {
        case 'kpi':
          return resource.analytics.kpi(params);
        case 'timeSeries':
          return resource.analytics.timeSeries(params);
        case 'leaderboard':
          return resource.analytics.leaderboard(params);
        case 'distribution':
          return resource.analytics.distribution(params);
      }
    }
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    invalidate: () => queryClient.invalidateQueries({ queryKey })
  };
}
